import { useTranslation } from "react-i18next";
import { Info, TrendingDown, TrendingUp } from "lucide-react";
import V2BusyFrame, { V2Skeleton } from "./V2BusyFrame";
import { formatScore } from "./v2OverviewUtils";

const CELL =
  "flex min-w-0 items-center border-r border-[rgba(98,94,166,0.2)] px-5 py-4 max-[900px]:border-b";

function Sparkline({ values, labels }) {
  const width = 196;
  const height = 42;
  const numeric = (values || []).filter((value) => Number.isFinite(value));
  const min = numeric.length ? Math.min(...numeric, 90) : 90;
  const max = numeric.length ? Math.max(...numeric, min + 1) : 100;
  const points = values.map((value, index) => {
    const x = (index / Math.max(values.length - 1, 1)) * (width - 8) + 4;
    const y = height - 8 - ((value - min) / (max - min)) * (height - 16);
    return `${x},${y}`;
  });
  const last = points[points.length - 1]?.split(",") || [];

  return (
    <svg className="v2-avail-spark" viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <line x1="4" y1={height / 2} x2={width - 4} y2={height / 2} stroke="#3D3890" strokeDasharray="3 4" />
      <polyline points={points.join(" ")} fill="none" stroke="#00FF8C" strokeWidth="2.2" />
      {last.length === 2 && <circle cx={last[0]} cy={last[1]} r="3.2" fill="#00FF8C" />}
      <title>{labels?.[labels.length - 1]}</title>
    </svg>
  );
}

function AvailabilityGauge({ available, total, percent }) {
  const radius = 27;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="v2-score-gauge-wrap" aria-hidden="true">
      <svg className="v2-score-gauge" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={radius} fill="none" stroke="#231E62" strokeWidth="5.5" />
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="#00FF8C"
          strokeWidth="5.5"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 32 32)"
          strokeLinecap="round"
        />
      </svg>
      <strong>{`${available}/${total}`}</strong>
    </div>
  );
}

export function V2AvailabilitySkeleton() {
  return (
    <section
      className="grid min-h-[112px] grid-cols-[minmax(280px,1.55fr)_repeat(3,minmax(150px,1fr))_minmax(190px,1.1fr)] rounded-[10px] border border-[rgba(98,94,166,0.45)] bg-[rgba(17,12,61,0.7)] max-[900px]:grid-cols-2"
      aria-hidden="true"
    >
      <div className={`${CELL} gap-4`}>
        <V2Skeleton className="h-[58px] w-[58px] rounded-full" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <V2Skeleton className="h-3.5 w-28" />
          <V2Skeleton className="h-3 w-40" />
        </div>
      </div>
      {[0, 1, 2].map((index) => (
        <div key={index} className={`${CELL} flex-col items-start justify-center gap-2`}>
          <V2Skeleton className="h-2.5 w-16" />
          <V2Skeleton className="h-8 w-20" />
          <V2Skeleton className="h-2.5 w-24" />
        </div>
      ))}
      <div className={`${CELL} flex-col items-start justify-center gap-2 border-r-0`}>
        <V2Skeleton className="h-2.5 w-20" />
        <V2Skeleton className="h-10 w-full" />
      </div>
    </section>
  );
}

export default function V2AvailabilityCard({ data, loading = false }) {
  const { t } = useTranslation();
  const absentPlayers = data.absentPlayers || (data.absentName ? [data.absentName] : []);
  const absentCount = data.absent ?? absentPlayers.length;
  const isPositive = data.avg7Delta >= 0;
  const DeltaIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <V2BusyFrame loading={loading} label={t("v2.overview.fetching")} className="rounded-[10px]">
    <section
      className="grid min-h-[112px] grid-cols-[minmax(280px,1.55fr)_repeat(3,minmax(150px,1fr))_minmax(190px,1.1fr)] rounded-[10px] border border-[rgba(98,94,166,0.45)] bg-[rgba(17,12,61,0.7)] max-[900px]:grid-cols-2"
      aria-label={t("v2.overview.availability")}
    >
      <div className={`${CELL} gap-4`}>
        <AvailabilityGauge available={data.available} total={data.total} percent={data.now} />
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.06em] text-[#f4f2ff]">
            <span>{t("v2.overview.availability")}</span>
            <span className="inline-flex text-[#8b86bc]" title={t("v2.overview.availability_hint")}>
              <Info size={13} />
            </span>
          </div>
          {absentCount > 0 ? (
            <div className="flex flex-col items-start gap-1.5">
              <span className="inline-flex items-center gap-[7px] text-[12px] font-semibold text-[#ff8a96]">
                <i className="inline-block h-2 w-2 rounded-sm bg-v2-bad" />
                {t("v2.overview.unavailable_count", { count: absentCount })}
              </span>
              <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                {absentPlayers.map((name) => (
                  <li
                    key={name}
                    className="rounded-[5px] border border-[rgba(255,77,94,0.38)] bg-[rgba(255,77,94,0.12)] px-2 py-[3px] text-[12px] font-semibold text-[#f6f3ff]"
                  >
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <span className="inline-flex items-center gap-[7px] text-[12px] font-semibold text-[#00d99a]">
              {t("v2.overview.all_available")}
            </span>
          )}
        </div>
      </div>

      <div className={`${CELL} flex-col items-start justify-center gap-[5px]`}>
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#b7b2e0]">{t("v2.overview.now")}</span>
        <strong className="font-display text-[34px] font-bold leading-[0.95] text-[#f8f7ff]">
          {formatScore(data.now)}
          <span className="ml-0.5 text-[18px] font-semibold text-[#c9c5f5]">%</span>
        </strong>
        <small className="text-[12px] leading-[1.35] text-[#8f8abf]">{data.nowMeta || t(data.nowMetaKey)}</small>
      </div>

      <div className={`${CELL} flex-col items-start justify-center gap-[5px]`}>
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#b7b2e0]">{t("v2.overview.avg_7_days")}</span>
        <strong className="font-display text-[34px] font-bold leading-[0.95] text-[#f8f7ff]">
          {formatScore(data.avg7)}
          <span className="ml-0.5 text-[18px] font-semibold text-[#c9c5f5]">%</span>
        </strong>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-[13px] font-bold ${isPositive ? "text-[#00d99a]" : "text-[#ff6e7d]"}`}>
            <DeltaIcon size={14} />
            {`${formatScore(Math.abs(data.avg7Delta))} ${t("v2.overview.pt")}`}
          </span>
          <span className="text-[12px] text-[#8f8abf]">{t("v2.overview.vs_previous_week")}</span>
        </div>
      </div>

      <div className={`${CELL} flex-col items-start justify-center gap-[5px]`}>
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#b7b2e0]">{t("v2.overview.avg_30_days")}</span>
        <strong className="font-display text-[34px] font-bold leading-[0.95] text-[#f8f7ff]">
          {formatScore(data.avg30)}
          <span className="ml-0.5 text-[18px] font-semibold text-[#c9c5f5]">%</span>
        </strong>
        <small className="text-[12px] leading-[1.35] text-[#8f8abf]">{data.avg30Meta || t(data.avg30MetaKey)}</small>
      </div>

      <div className={`${CELL} flex-col items-start justify-center gap-[5px] border-r-0`}>
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#b7b2e0]">{t("v2.overview.trend_10_days")}</span>
        <Sparkline values={data.trend} labels={data.trendLabels} />
      </div>
    </section>
    </V2BusyFrame>
  );
}
